import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, DatePicker, Input, Select, Table, Tabs, Tooltip, message } from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  ClearOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { API_URL } from "../../constants";
import "./workingmonth.css";

const { RangePicker } = DatePicker;

const Badge = ({ tone = "neutral", children }) => (
  <span className={`wm-badge wm-badge-${tone}`}>{children}</span>
);

const APP_UI = {
  admin: { tone: "accent", label: "Admin" },
  gestionar: { tone: "good", label: "Gestionar" },
  site: { tone: "neutral", label: "Site" },
  paynet: { tone: "warn", label: "Paynet" },
  client: { tone: "neutral", label: "Client" },
};
const TYPE_UI = {
  click: { tone: "accent", label: "Click" },
  page: { tone: "neutral", label: "Pagină" },
  submit: { tone: "good", label: "Formular" },
  error: { tone: "bad", label: "Eroare JS" },
};
const STATUS_OPTIONS = [
  { value: "2xx", label: "2xx – reușite" },
  { value: "3xx", label: "3xx – redirecționări" },
  { value: "4xx", label: "4xx – refuzate / negăsite" },
  { value: "5xx", label: "5xx – erori server" },
  { value: "errors", label: "Toate erorile (≥ 400)" },
];
const METHOD_OPTIONS = ["GET", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"].map((m) => ({
  value: m,
  label: m,
}));

const RANGE_PRESETS = [
  { label: "Azi", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
  {
    label: "Ieri",
    value: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
  },
  { label: "Ultimele 7 zile", value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Ultimele 30 zile", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Luna curentă", value: [dayjs().startOf("month"), dayjs().endOf("day")] },
  {
    label: "Luna trecută",
    value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")],
  },
];

const fmt = (d, withSec = true) =>
  d ? dayjs(d).format(withSec ? "DD.MM.YYYY, HH:mm:ss" : "DD.MM.YYYY, HH:mm") : "—";

const statusTone = (s) => (s < 300 ? "good" : s < 400 ? "neutral" : s < 500 ? "warn" : "bad");

const userName = (u) => (u ? `${u.nume || ""} ${u.prenume || ""}`.trim() || u.email : "");

const UserCell = ({ user, userId }) => {
  if (!user && userId == null) return <span style={{ color: "#8b93a1" }}>anonim</span>;
  if (!user) return <span className="wm-log-email">#{userId}</span>;
  return (
    <div className="wm-user-cell">
      <div className="wm-user-name">{userName(user)}</div>
      <div className="wm-user-email">{user.email}</div>
    </div>
  );
};

const pretty = (txt) => {
  if (!txt) return null;
  try {
    return JSON.stringify(JSON.parse(txt), null, 2);
  } catch (e) {
    return txt;
  }
};

const Pre = ({ children }) => (
  <pre
    style={{
      background: "#f4f5f7",
      border: "1px solid #e7e8ea",
      borderRadius: 8,
      padding: 10,
      maxHeight: 280,
      overflow: "auto",
      fontSize: 11.5,
      margin: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-all",
    }}
  >
    {children}
  </pre>
);

const Detail = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#8b93a1",
        marginBottom: 2,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 12.5, wordBreak: "break-all" }}>{children || "—"}</div>
  </div>
);

const EMPTY_REQ = { userId: undefined, method: undefined, status: undefined, app: undefined, ip: "", route: "", q: "" };
const EMPTY_EV = { userId: undefined, app: undefined, type: undefined, page: "", q: "" };
const EMPTY_SES = { userId: undefined, app: undefined, ip: "" };

// durata unei sesiuni, pe scurt
const fmtDur = (a, b) => {
  const s = Math.max(0, Math.round((new Date(b) - new Date(a)) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
};

const pager = (page, pageSize, total, onChange) => ({
  current: page,
  pageSize,
  total,
  onChange,
  pageSizeOptions: ["20", "50", "100", "200"],
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (t, r) => `${r[0]}–${r[1]} din ${t} înregistrări`,
});

const AuditLog = () => {
  const accessToken = useSelector((s) => s.user.currentUser?.accessToken);
  const auth = useMemo(
    () => ({ headers: { Authorization: "Bearer " + accessToken } }),
    [accessToken]
  );

  const [range, setRange] = useState([dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("sessions");
  const [sessionId, setSessionId] = useState(null); // filtru comun (din detaliile unui rând)
  const [noOptions, setNoOptions] = useState(true); // ascunde preflight-urile CORS (OPTIONS)

  // latimea vizibila a cardului: continutul randurilor expandate se "lipeste"
  // de marginea din stanga (sticky) si ramane in zona vizibila chiar daca
  // tabelul are scroll orizontal - nu iese din card
  const [cardWidth, setCardWidth] = useState(0);
  const cardObs = useRef(null);
  const cardRef = useCallback((node) => {
    if (cardObs.current) cardObs.current.disconnect();
    cardObs.current = null;
    if (node && typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => setCardWidth(node.clientWidth));
      ro.observe(node);
      cardObs.current = ro;
      setCardWidth(node.clientWidth);
    }
  }, []);
  const stickyStyle = {
    position: "sticky",
    left: 0,
    minWidth: 0,
    width: cardWidth ? cardWidth - 72 : "auto",
  };

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [reqFilters, setReqFilters] = useState(EMPTY_REQ);
  const [reqPage, setReqPage] = useState({ page: 1, pageSize: 50 });
  const [requests, setRequests] = useState({ rows: [], total: 0 });
  const [reqLoading, setReqLoading] = useState(false);

  const [evFilters, setEvFilters] = useState(EMPTY_EV);
  const [evPage, setEvPage] = useState({ page: 1, pageSize: 50 });
  const [events, setEvents] = useState({ rows: [], total: 0 });
  const [evLoading, setEvLoading] = useState(false);

  const [sesFilters, setSesFilters] = useState(EMPTY_SES);
  const [sesPage, setSesPage] = useState({ page: 1, pageSize: 30 });
  const [sessions, setSessions] = useState({ rows: [], total: 0, withoutSession: 0 });
  const [sesLoading, setSesLoading] = useState(false);
  const [timelines, setTimelines] = useState({}); // sessionId -> { loading, items, truncated }
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);

  const rangeParams = useCallback(
    (r = range) =>
      r && r[0] && r[1]
        ? { minDate: r[0].startOf("day").toISOString(), maxDate: r[1].endOf("day").toISOString() }
        : {},
    [range]
  );

  const qs = (obj) => {
    const p = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") p.set(k, v);
    });
    return p.toString();
  };

  const fail = (what) => message.error(`Nu am putut încărca ${what}.`);

  useEffect(() => {
    axios
      .get(API_URL + "audit/users", auth)
      .then((r) => setUsers(r.data || []))
      .catch(() => {});
  }, [auth]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const r = await axios.get(API_URL + "audit/stats?" + qs(rangeParams()), auth);
      setStats(r.data);
    } catch (e) {
      fail("sumarul");
    } finally {
      setStatsLoading(false);
    }
  }, [auth, rangeParams]);

  const fetchRequests = useCallback(async () => {
    setReqLoading(true);
    try {
      const r = await axios.get(
        API_URL +
          "audit/requests?" +
          qs({
            ...rangeParams(),
            ...reqFilters,
            sessionId,
            noOptions: noOptions ? "1" : "",
            take: reqPage.pageSize,
            skip: (reqPage.page - 1) * reqPage.pageSize,
          }),
        auth
      );
      setRequests({ rows: r.data.rows, total: r.data.total });
    } catch (e) {
      fail("request-urile");
    } finally {
      setReqLoading(false);
    }
  }, [auth, rangeParams, reqFilters, reqPage, sessionId, noOptions]);

  const fetchEvents = useCallback(async () => {
    setEvLoading(true);
    try {
      const r = await axios.get(
        API_URL +
          "audit/events?" +
          qs({
            ...rangeParams(),
            ...evFilters,
            sessionId,
            take: evPage.pageSize,
            skip: (evPage.page - 1) * evPage.pageSize,
          }),
        auth
      );
      setEvents({ rows: r.data.rows, total: r.data.total });
    } catch (e) {
      fail("evenimentele");
    } finally {
      setEvLoading(false);
    }
  }, [auth, rangeParams, evFilters, evPage, sessionId]);

  const fetchSessions = useCallback(async () => {
    setSesLoading(true);
    try {
      const r = await axios.get(
        API_URL +
          "audit/sessions?" +
          qs({
            ...rangeParams(),
            ...sesFilters,
            take: sesPage.pageSize,
            skip: (sesPage.page - 1) * sesPage.pageSize,
          }),
        auth
      );
      setSessions({ rows: r.data.rows, total: r.data.total, withoutSession: r.data.withoutSession });
    } catch (e) {
      fail("sesiunile");
    } finally {
      setSesLoading(false);
    }
  }, [auth, rangeParams, sesFilters, sesPage]);

  const fetchRoutes = useCallback(async () => {
    setRoutesLoading(true);
    try {
      const r = await axios.get(
        API_URL + "audit/routes?" + qs({ ...rangeParams(), noOptions: noOptions ? "1" : "" }),
        auth
      );
      setRoutes(r.data);
    } catch (e) {
      fail("sumarul pe rute");
    } finally {
      setRoutesLoading(false);
    }
  }, [auth, rangeParams, noOptions]);

  // cronologia unei sesiuni se incarca la expandarea randului (o singura data)
  const loadTimeline = async (sid) => {
    if (timelines[sid] && !timelines[sid].error) return;
    setTimelines((t) => ({ ...t, [sid]: { loading: true, items: [] } }));
    try {
      const r = await axios.get(
        API_URL +
          `audit/sessions/${encodeURIComponent(sid)}?` +
          qs({ ...rangeParams(), noOptions: noOptions ? "1" : "" }),
        auth
      );
      setTimelines((t) => ({ ...t, [sid]: { loading: false, ...r.data } }));
    } catch (e) {
      setTimelines((t) => ({ ...t, [sid]: { loading: false, items: [], error: true } }));
      fail("cronologia sesiunii");
    }
  };

  const setSes = (patch) => {
    setSesFilters((f) => ({ ...f, ...patch }));
    setSesPage((p) => ({ ...p, page: 1 }));
  };

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const reloadAll = () => {
    setTimelines({});
    fetchStats();
    fetchSessions();
    fetchRequests();
    fetchEvents();
    fetchRoutes();
  };

  const handleRangeChange = (r) => {
    if (!r || !r[0] || !r[1]) return;
    setRange(r);
    setReqPage((p) => ({ ...p, page: 1 }));
    setEvPage((p) => ({ ...p, page: 1 }));
  };

  const setReq = (patch) => {
    setReqFilters((f) => ({ ...f, ...patch }));
    setReqPage((p) => ({ ...p, page: 1 }));
  };
  const setEv = (patch) => {
    setEvFilters((f) => ({ ...f, ...patch }));
    setEvPage((p) => ({ ...p, page: 1 }));
  };

  const pickSession = (sid) => {
    setSessionId(sid);
    setReqPage((p) => ({ ...p, page: 1 }));
    setEvPage((p) => ({ ...p, page: 1 }));
  };

  const exportCsv = async (kind) => {
    const params =
      kind === "requests"
        ? { ...rangeParams(), ...reqFilters, sessionId, noOptions: noOptions ? "1" : "" }
        : { ...rangeParams(), ...evFilters, sessionId };
    try {
      const res = await axios.get(API_URL + `audit/${kind}/export?` + qs(params), {
        ...auth,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `audit_${kind === "requests" ? "requesturi" : "evenimente"}_${range[0].format("YYYYMMDD")}-${range[1].format("YYYYMMDD")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Export CSV descărcat (toate înregistrările din filtrul curent).");
    } catch (e) {
      message.error("Exportul CSV a eșuat.");
    }
  };

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: `${userName(u)} (${u.email})${u.active === false ? " – dezactivat" : ""}`,
      })),
    [users]
  );
  const appOptions = Object.entries(APP_UI).map(([value, a]) => ({ value, label: a.label }));

  const reqActive = Object.values(reqFilters).some((v) => v !== undefined && v !== "") || sessionId;
  const evActive = Object.values(evFilters).some((v) => v !== undefined && v !== "") || sessionId;

  // ---------------------------------------------------------------- request-uri
  const reqColumns = [
    {
      title: "Data",
      dataIndex: "ts",
      width: 150,
      render: (v) => <span className="wm-log-time">{fmt(v)}</span>,
    },
    {
      title: "Utilizator",
      width: 170,
      ellipsis: true,
      render: (r) => <UserCell user={r.user} userId={r.userId} />,
    },
    {
      title: "Request",
      width: 330,
      ellipsis: true,
      render: (r) => (
        <Tooltip title={r.url}>
          <div style={{ minWidth: 0 }}>
            <span className="wm-log-time" style={{ fontWeight: 700, marginRight: 6 }}>
              {r.method}
            </span>
            <span style={{ fontSize: 12.5 }}>{r.url}</span>
            {r.route ? (
              <div style={{ fontSize: 11, color: "#8b93a1" }}>{r.route}</div>
            ) : null}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 90,
      render: (v) => <Badge tone={statusTone(v)}>{v}</Badge>,
    },
    {
      title: "Aplicație / pagină",
      width: 150,
      ellipsis: true,
      render: (r) => (
        <div>
          {r.app ? <Badge tone={(APP_UI[r.app] || {}).tone}>{(APP_UI[r.app] || { label: r.app }).label}</Badge> : "—"}
          {r.page ? <div style={{ fontSize: 11, color: "#5c6470" }}>{r.page}</div> : null}
        </div>
      ),
    },
    {
      title: "IP",
      dataIndex: "ip",
      width: 130,
      render: (v) => (
        <span
          className="wm-log-email"
          style={{ cursor: "pointer" }}
          title="Filtrează după acest IP"
          onClick={() => setReq({ ip: v })}
        >
          {v || "—"}
        </span>
      ),
    },
    {
      title: "Durată",
      dataIndex: "durationMs",
      width: 80,
      align: "right",
      render: (v) => <span className="wm-log-time">{v != null ? `${v} ms` : "—"}</span>,
    },
  ];

  const reqExpanded = (r) => (
    <div style={{ ...stickyStyle, display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px 20px" }}>
        <Detail label="URL complet">{r.url}</Detail>
        <Detail label="Rută">{r.route}</Detail>
        <Detail label="IP (lanț proxy)">{r.ipChain}</Detail>
        <Detail label="Sesiune">
          {r.sessionId ? (
            <span
              className="wm-log-email"
              style={{ cursor: "pointer" }}
              title="Arată tot ce s-a întâmplat în această sesiune"
              onClick={() => pickSession(r.sessionId)}
            >
              {r.sessionId}
            </span>
          ) : null}
        </Detail>
        <Detail label="Referer">{r.referer}</Detail>
        <Detail label="Răspuns">{r.resBytes != null ? `${r.resBytes} bytes` : null}</Detail>
        <Detail label="User-Agent">{r.userAgent}</Detail>
      </div>
      {r.error ? (
        <Detail label="Eroare">
          <span style={{ color: "#b42318" }}>{r.error}</span>
        </Detail>
      ) : null}
      {r.body ? (
        <Detail label="Body (parolele sunt mascate)">
          <Pre>{pretty(r.body)}</Pre>
        </Detail>
      ) : null}
    </div>
  );

  const requestsTab = (
    <>
      <p className="wm-tab-desc">
        Fiecare request primit de server: cine (din token), când, de unde (IP, aplicație, pagină),
        ce a cerut și ce a primit. Click pe „+” pentru toate detaliile, pe IP pentru a filtra,
        pe sesiune pentru a vedea tot ce a făcut acel browser.
      </p>
      <div className="wm-filters">
        <div className="wm-filter">
          <span className="wm-filter-label">Utilizator</span>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Toți"
            style={{ minWidth: 220 }}
            value={reqFilters.userId}
            onChange={(v) => setReq({ userId: v })}
            options={userOptions}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Metodă</span>
          <Select
            allowClear
            placeholder="Toate"
            style={{ minWidth: 110 }}
            value={reqFilters.method}
            onChange={(v) => setReq({ method: v })}
            options={METHOD_OPTIONS}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Status</span>
          <Select
            allowClear
            placeholder="Toate"
            style={{ minWidth: 190 }}
            value={reqFilters.status}
            onChange={(v) => setReq({ status: v })}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Aplicație</span>
          <Select
            allowClear
            placeholder="Toate"
            style={{ minWidth: 130 }}
            value={reqFilters.app}
            onChange={(v) => setReq({ app: v })}
            options={appOptions}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">IP</span>
          <Input
            allowClear
            placeholder="ex. 86.105."
            style={{ width: 140 }}
            value={reqFilters.ip}
            onChange={(e) => setReq({ ip: e.target.value })}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Rută</span>
          <Input
            allowClear
            placeholder="ex. /list/abons"
            style={{ width: 200 }}
            value={reqFilters.route}
            onChange={(e) => setReq({ route: e.target.value })}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Preflight CORS</span>
          <Checkbox
            checked={noOptions}
            onChange={(e) => {
              setNoOptions(e.target.checked);
              setReqPage((p) => ({ ...p, page: 1 }));
            }}
          >
            fără OPTIONS
          </Checkbox>
        </div>
        <div className="wm-filter" style={{ flex: "1 1 220px" }}>
          <span className="wm-filter-label">Căutare (URL, body, eroare, user-agent)</span>
          <Input.Search
            allowClear
            placeholder="Apăsați Enter pentru a căuta"
            defaultValue={reqFilters.q}
            onSearch={(v) => setReq({ q: v })}
          />
        </div>
      </div>
      <Toolbar
        total={requests.total}
        what="request-uri"
        active={reqActive}
        onReset={() => {
          setReqFilters(EMPTY_REQ);
          setSessionId(null);
          setReqPage((p) => ({ ...p, page: 1 }));
        }}
        onExport={() => exportCsv("requests")}
        onReload={fetchRequests}
      />
      <Table
        rowKey="id"
        size="small"
        loading={reqLoading}
        columns={reqColumns}
        dataSource={requests.rows}
        pagination={pager(reqPage.page, reqPage.pageSize, requests.total, (page, pageSize) =>
          setReqPage({ page, pageSize })
        )}
        expandable={{ expandedRowRender: reqExpanded }}
        locale={{ emptyText: "Niciun request în intervalul / filtrele selectate" }}
        scroll={{ x: 1150 }}
      />
    </>
  );

  // ---------------------------------------------------------------- evenimente
  const evColumns = [
    {
      title: "Data",
      dataIndex: "ts",
      width: 150,
      render: (v) => <span className="wm-log-time">{fmt(v)}</span>,
    },
    {
      title: "Utilizator",
      width: 170,
      ellipsis: true,
      render: (r) => <UserCell user={r.user} userId={r.userId} />,
    },
    {
      title: "Aplicație",
      dataIndex: "app",
      width: 100,
      render: (v) => <Badge tone={(APP_UI[v] || {}).tone}>{(APP_UI[v] || { label: v }).label}</Badge>,
    },
    {
      title: "Tip",
      dataIndex: "type",
      width: 100,
      render: (v) => <Badge tone={(TYPE_UI[v] || {}).tone}>{(TYPE_UI[v] || { label: v }).label}</Badge>,
    },
    {
      title: "Acțiune",
      ellipsis: true,
      render: (r) => (
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: 12.5, fontWeight: r.type === "click" ? 600 : 400 }}>{r.label || "—"}</span>
          {r.target ? <div style={{ fontSize: 11, color: "#8b93a1" }}>{r.target}</div> : null}
        </div>
      ),
    },
    {
      title: "Pagină",
      dataIndex: "page",
      width: 160,
      ellipsis: true,
      render: (v) => (
        <span className="wm-log-email" style={{ cursor: "pointer" }} onClick={() => setEv({ page: v })}>
          {v || "—"}
        </span>
      ),
    },
    { title: "IP", dataIndex: "ip", width: 120, render: (v) => <span className="wm-log-email">{v || "—"}</span> },
  ];

  const evExpanded = (r) => (
    <div style={{ ...stickyStyle, display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px 20px" }}>
        <Detail label="Element">{r.target}</Detail>
        <Detail label="Moment în browser">{fmt(r.clientTs)}</Detail>
        <Detail label="Sesiune">
          {r.sessionId ? (
            <span
              className="wm-log-email"
              style={{ cursor: "pointer" }}
              title="Arată tot ce s-a întâmplat în această sesiune"
              onClick={() => pickSession(r.sessionId)}
            >
              {r.sessionId}
            </span>
          ) : null}
        </Detail>
        <Detail label="User-Agent">{r.userAgent}</Detail>
      </div>
      {r.meta ? (
        <Detail label="Detalii">
          <Pre>{pretty(r.meta)}</Pre>
        </Detail>
      ) : null}
    </div>
  );

  const eventsTab = (
    <>
      <p className="wm-tab-desc">
        Ce s-a făcut efectiv în interfață (admin și gestionar): butoanele apăsate, paginile deschise,
        formularele trimise, erorile din browser. Conținutul câmpurilor nu se înregistrează niciodată.
      </p>
      <div className="wm-filters">
        <div className="wm-filter">
          <span className="wm-filter-label">Utilizator</span>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Toți"
            style={{ minWidth: 220 }}
            value={evFilters.userId}
            onChange={(v) => setEv({ userId: v })}
            options={userOptions}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Aplicație</span>
          <Select
            allowClear
            placeholder="Toate"
            style={{ minWidth: 130 }}
            value={evFilters.app}
            onChange={(v) => setEv({ app: v })}
            options={[
              { value: "admin", label: "Admin" },
              { value: "gestionar", label: "Gestionar" },
            ]}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Tip</span>
          <Select
            allowClear
            placeholder="Toate"
            style={{ minWidth: 130 }}
            value={evFilters.type}
            onChange={(v) => setEv({ type: v })}
            options={Object.entries(TYPE_UI).map(([value, t]) => ({ value, label: t.label }))}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Pagină</span>
          <Input
            allowClear
            placeholder="ex. /dashboard/invoice"
            style={{ width: 190 }}
            value={evFilters.page}
            onChange={(e) => setEv({ page: e.target.value })}
          />
        </div>
        <div className="wm-filter" style={{ flex: "1 1 220px" }}>
          <span className="wm-filter-label">Căutare (buton, element, detalii)</span>
          <Input.Search
            allowClear
            placeholder="Apăsați Enter pentru a căuta"
            defaultValue={evFilters.q}
            onSearch={(v) => setEv({ q: v })}
          />
        </div>
      </div>
      <Toolbar
        total={events.total}
        what="evenimente"
        active={evActive}
        onReset={() => {
          setEvFilters(EMPTY_EV);
          setSessionId(null);
          setEvPage((p) => ({ ...p, page: 1 }));
        }}
        onExport={() => exportCsv("events")}
        onReload={fetchEvents}
      />
      <Table
        rowKey="id"
        size="small"
        loading={evLoading}
        columns={evColumns}
        dataSource={events.rows}
        pagination={pager(evPage.page, evPage.pageSize, events.total, (page, pageSize) =>
          setEvPage({ page, pageSize })
        )}
        expandable={{ expandedRowRender: evExpanded }}
        locale={{ emptyText: "Niciun eveniment în intervalul / filtrele selectate" }}
        scroll={{ x: 1100 }}
      />
    </>
  );

  // ---------------------------------------------------------------- IP-uri / utilizatori
  const ipColumns = [
    {
      title: "IP",
      dataIndex: "ip",
      render: (v) => (
        <span
          className="wm-log-email"
          style={{ cursor: "pointer" }}
          title="Arată request-urile de la acest IP"
          onClick={() => {
            setReq({ ip: v || "" });
            setTab("requests");
          }}
        >
          {v || "—"}
        </span>
      ),
    },
    { title: "Request-uri", dataIndex: "requests", width: 110, align: "right", sorter: (a, b) => a.requests - b.requests, defaultSortOrder: "descend" },
    {
      title: "Utilizatori",
      render: (r) =>
        r.users.length ? (
          <Tooltip title={r.users.map((u) => u.email).join(", ")}>
            <span style={{ fontSize: 12.5 }}>
              {r.users.slice(0, 2).map(userName).join(", ")}
              {r.users.length > 2 ? ` +${r.users.length - 2}` : ""}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: "#8b93a1" }}>doar anonim</span>
        ),
    },
    {
      title: "Logări eșuate",
      dataIndex: "failedLogins",
      width: 120,
      align: "right",
      sorter: (a, b) => a.failedLogins - b.failedLogins,
      render: (v) => (v ? <Badge tone="bad">{v}</Badge> : <span style={{ color: "#8b93a1" }}>0</span>),
    },
    { title: "Prima", dataIndex: "first", width: 150, render: (v) => <span className="wm-log-time">{fmt(v, false)}</span> },
    { title: "Ultima", dataIndex: "last", width: 150, render: (v) => <span className="wm-log-time">{fmt(v, false)}</span> },
  ];

  const userColumns = [
    {
      title: "Utilizator",
      render: (r) => (
        <span
          style={{ cursor: "pointer" }}
          title="Arată request-urile acestui utilizator"
          onClick={() => {
            setReq({ userId: r.userId });
            setTab("requests");
          }}
        >
          <UserCell user={r.user} userId={r.userId} />
        </span>
      ),
    },
    { title: "Request-uri", dataIndex: "requests", width: 110, align: "right", sorter: (a, b) => a.requests - b.requests, defaultSortOrder: "descend" },
    {
      title: "IP-uri",
      width: 220,
      render: (r) => (
        <Tooltip title={r.ips.join(", ")}>
          <span className="wm-log-email">
            {r.ips.slice(0, 2).join(", ")}
            {r.ips.length > 2 ? ` +${r.ips.length - 2}` : ""}
          </span>
        </Tooltip>
      ),
    },
    { title: "Prima", dataIndex: "first", width: 150, render: (v) => <span className="wm-log-time">{fmt(v, false)}</span> },
    { title: "Ultima", dataIndex: "last", width: 150, render: (v) => <span className="wm-log-time">{fmt(v, false)}</span> },
  ];

  const sourcesTab = (
    <>
      <p className="wm-tab-desc">
        De unde a venit traficul în intervalul selectat. Un IP cu multe logări eșuate sau un
        utilizator cu IP-uri neobișnuite sunt primele lucruri de verificat după un incident.
      </p>
      {/* unul sub altul, fiecare cu scroll propriu: tabelele nu ies din card */}
      <div style={{ display: "grid", gap: 20, minWidth: 0 }}>
        <div style={{ minWidth: 0 }}>
          <h4 className="wm-card-title">IP-uri ({stats?.ips?.length || 0})</h4>
          <Table
            rowKey="ip"
            size="small"
            loading={statsLoading}
            columns={ipColumns}
            dataSource={stats?.ips || []}
            pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }}
            locale={{ emptyText: "Nicio activitate" }}
            scroll={{ x: 820 }}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <h4 className="wm-card-title">Utilizatori ({stats?.users?.length || 0})</h4>
          <Table
            rowKey="userId"
            size="small"
            loading={statsLoading}
            columns={userColumns}
            dataSource={stats?.users || []}
            pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }}
            locale={{ emptyText: "Nicio activitate" }}
            scroll={{ x: 820 }}
          />
        </div>
      </div>
    </>
  );

  // ---------------------------------------------------------------- sesiuni
  const sesColumns = [
    {
      title: "Început",
      dataIndex: "start",
      width: 140,
      render: (v) => <span className="wm-log-time">{fmt(v, false)}</span>,
    },
    {
      title: "Durată",
      width: 80,
      render: (r) => <span className="wm-log-time">{fmtDur(r.start, r.end)}</span>,
    },
    {
      title: "Utilizator",
      width: 160,
      ellipsis: true,
      render: (r) => <UserCell user={r.user} userId={r.userId} />,
    },
    {
      title: "Aplicație",
      dataIndex: "app",
      width: 100,
      render: (v) =>
        v ? <Badge tone={(APP_UI[v] || {}).tone}>{(APP_UI[v] || { label: v }).label}</Badge> : "—",
    },
    {
      title: "IP",
      width: 150,
      render: (r) => (
        <span
          className="wm-log-email"
          style={{ cursor: "pointer" }}
          title="Filtrează sesiunile după acest IP"
          onClick={() => setSes({ ip: r.ip || "" })}
        >
          {r.ip || "—"}
          {r.ips > 1 ? (
            <>
              {" "}
              <Badge tone="warn">{r.ips} IP-uri</Badge>
            </>
          ) : null}
        </span>
      ),
    },
    {
      title: "Request-uri",
      width: 120,
      align: "right",
      render: (r) => (
        <span className="wm-log-time">
          {r.requests - r.preflights}
          {r.errors ? (
            <>
              {" "}
              <Badge tone="bad">{r.errors} erori</Badge>
            </>
          ) : null}
        </span>
      ),
    },
    {
      title: "Click-uri",
      dataIndex: "clicks",
      width: 80,
      align: "right",
      render: (v) => <span className="wm-log-time">{v}</span>,
    },
    {
      title: "Pagini",
      dataIndex: "pages",
      width: 70,
      align: "right",
      render: (v) => <span className="wm-log-time">{v}</span>,
    },
    {
      title: "Erori JS",
      dataIndex: "jsErrors",
      width: 80,
      align: "right",
      render: (v) => (v ? <Badge tone="bad">{v}</Badge> : <span style={{ color: "#8b93a1" }}>0</span>),
    },
  ];

  const renderTimeline = (sid) => {
    const tl = timelines[sid];
    if (!tl || tl.loading) {
      return <div style={{ fontSize: 12.5, color: "#8b93a1" }}>Se încarcă cronologia…</div>;
    }
    return (
      <div style={{ ...stickyStyle, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span className="wm-results-note" style={{ margin: 0, flex: "1 1 auto" }}>
            Sesiune <span className="wm-log-email">{sid}</span> · {tl.items.length} intrări
            {tl.truncated ? " (afișate ultimele 500 request-uri / 500 evenimente)" : ""}
          </span>
          <button
            type="button"
            className="wm-btn wm-btn-secondary wm-btn-sm"
            onClick={() => {
              pickSession(sid);
              setTab("requests");
            }}
          >
            Toate request-urile
          </button>
          <button
            type="button"
            className="wm-btn wm-btn-secondary wm-btn-sm"
            onClick={() => {
              pickSession(sid);
              setTab("events");
            }}
          >
            Toate evenimentele
          </button>
        </div>
        <div
          style={{
            maxHeight: 420,
            overflow: "auto",
            border: "1px solid #e7e8ea",
            borderRadius: 8,
            background: "#fff",
          }}
        >
          {tl.items.map((i) => (
            <div
              key={`${i.kind}-${i.id}`}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "baseline",
                padding: "4px 10px",
                borderBottom: "1px solid #f0f1f3",
                fontSize: 12.5,
                background: i.kind === "event" ? "#fff8f1" : "transparent",
              }}
            >
              <span className="wm-log-time" style={{ flex: "0 0 66px" }}>
                {dayjs(i.ts).format("HH:mm:ss")}
              </span>
              <span style={{ flex: "0 0 110px" }}>
                {i.kind === "event" ? (
                  <Badge tone={(TYPE_UI[i.type] || {}).tone}>
                    {(TYPE_UI[i.type] || { label: i.type }).label}
                  </Badge>
                ) : (
                  <Badge tone={statusTone(i.status)}>
                    {i.method} {i.status}
                  </Badge>
                )}
              </span>
              <span style={{ flex: "1 1 auto", minWidth: 0, wordBreak: "break-all" }}>
                {i.kind === "event" ? (
                  <>
                    <span style={{ fontWeight: i.type === "click" ? 600 : 400 }}>{i.label}</span>
                    {i.type === "page" ? <span style={{ color: "#8b93a1" }}> · {i.page}</span> : null}
                    {i.target && i.type !== "page" ? (
                      <span style={{ color: "#8b93a1" }}> · {i.target}</span>
                    ) : null}
                  </>
                ) : (
                  <>
                    {i.url}
                    {i.error ? <span style={{ color: "#b42318" }}> · {i.error}</span> : null}
                  </>
                )}
              </span>
              <span
                className="wm-log-time"
                style={{ flex: "0 0 60px", textAlign: "right", color: "#8b93a1" }}
              >
                {i.kind === "request" && i.durationMs != null ? `${i.durationMs} ms` : ""}
              </span>
            </div>
          ))}
          {!tl.items.length ? (
            <div style={{ padding: 10, color: "#8b93a1" }}>Nimic în intervalul selectat.</div>
          ) : null}
        </div>
      </div>
    );
  };

  const sesActive = Object.values(sesFilters).some((v) => v !== undefined && v !== "");
  const sessionsTab = (
    <>
      <p className="wm-tab-desc">
        O vizită = un rând: tot ce a făcut un browser de la deschidere până la închidere
        (request-uri și click-uri, grupate). Click pe „+” pentru cronologia completă a sesiunii;
        un rând cu mai multe IP-uri sau cu erori multe e de verificat primul.
      </p>
      <div className="wm-filters">
        <div className="wm-filter">
          <span className="wm-filter-label">Utilizator</span>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Toți"
            style={{ minWidth: 220 }}
            value={sesFilters.userId}
            onChange={(v) => setSes({ userId: v })}
            options={userOptions}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Aplicație</span>
          <Select
            allowClear
            placeholder="Toate"
            style={{ minWidth: 130 }}
            value={sesFilters.app}
            onChange={(v) => setSes({ app: v })}
            options={appOptions}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">IP</span>
          <Input
            allowClear
            placeholder="ex. 86.105."
            style={{ width: 160 }}
            value={sesFilters.ip}
            onChange={(e) => setSes({ ip: e.target.value })}
          />
        </div>
      </div>
      <Toolbar
        total={sessions.total}
        what="sesiuni"
        active={sesActive}
        note={
          sessions.withoutSession
            ? `${sessions.withoutSession} request-uri fără sesiune (webhook-uri, scripturi, site) sunt doar în tab-ul Request-uri API`
            : null
        }
        onReset={() => {
          setSesFilters(EMPTY_SES);
          setSesPage((p) => ({ ...p, page: 1 }));
        }}
        onReload={fetchSessions}
      />
      <Table
        rowKey="sessionId"
        size="small"
        loading={sesLoading}
        columns={sesColumns}
        dataSource={sessions.rows}
        pagination={pager(sesPage.page, sesPage.pageSize, sessions.total, (page, pageSize) =>
          setSesPage({ page, pageSize })
        )}
        expandable={{
          expandedRowRender: (r) => renderTimeline(r.sessionId),
          onExpand: (expanded, r) => expanded && loadTimeline(r.sessionId),
        }}
        locale={{ emptyText: "Nicio sesiune în intervalul / filtrele selectate" }}
        scroll={{ x: 1000 }}
      />
    </>
  );

  // ---------------------------------------------------------------- rute
  const numSorter = (k) => (a, b) => (a[k] || 0) - (b[k] || 0);
  const routeColumns = [
    {
      title: "Metodă",
      dataIndex: "method",
      width: 85,
      render: (v) => <span className="wm-log-time" style={{ fontWeight: 700 }}>{v}</span>,
    },
    {
      title: "Rută",
      dataIndex: "route",
      ellipsis: true,
      render: (v) => (
        <span
          className="wm-log-email"
          style={{ cursor: "pointer" }}
          title="Arată request-urile acestei rute"
          onClick={() => {
            setReq({ route: v || "" });
            setTab("requests");
          }}
        >
          {v || "—"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      render: (v) => <Badge tone={statusTone(v)}>{v}</Badge>,
    },
    {
      title: "Request-uri",
      dataIndex: "requests",
      width: 100,
      align: "right",
      sorter: numSorter("requests"),
      defaultSortOrder: "descend",
      render: (v) => <span className="wm-log-time">{v}</span>,
    },
    { title: "Utilizatori", dataIndex: "users", width: 95, align: "right", sorter: numSorter("users") },
    { title: "Sesiuni", dataIndex: "sessions", width: 85, align: "right", sorter: numSorter("sessions") },
    { title: "IP-uri", dataIndex: "ips", width: 75, align: "right", sorter: numSorter("ips") },
    {
      title: "Medie",
      dataIndex: "avgMs",
      width: 80,
      align: "right",
      sorter: numSorter("avgMs"),
      render: (v) => <span className="wm-log-time">{v} ms</span>,
    },
    {
      title: "Max",
      dataIndex: "maxMs",
      width: 80,
      align: "right",
      sorter: numSorter("maxMs"),
      render: (v) => <span className="wm-log-time">{v} ms</span>,
    },
    {
      title: "Ultima",
      dataIndex: "last",
      width: 140,
      render: (v) => <span className="wm-log-time">{fmt(v, false)}</span>,
    },
  ];

  const routesTab = (
    <>
      <p className="wm-tab-desc">
        Câte request-uri a primit fiecare rută (și cu ce status) în interval. Rutele cu mii de
        request-uri de la puține sesiuni sunt polling sau retry-uri din interfață; cele cu 4xx/5xx
        sunt de verificat. Click pe rută pentru lista request-urilor ei.
      </p>
      <Toolbar total={routes.length} what="rute" active={false} onReload={fetchRoutes} />
      <Table
        rowKey={(r) => `${r.method} ${r.route} ${r.status}`}
        size="small"
        loading={routesLoading}
        columns={routeColumns}
        dataSource={routes}
        pagination={{
          defaultPageSize: 30,
          pageSizeOptions: ["30", "50", "100", "300"],
          showSizeChanger: true,
          showTotal: (tt, r2) => `${r2[0]}–${r2[1]} din ${tt} rute`,
        }}
        locale={{ emptyText: "Niciun request în interval" }}
        scroll={{ x: 1000 }}
      />
    </>
  );

  const t = stats?.totals || {};
  return (
    <div className="wm-page">
      <div className="wm-filters" style={{ marginBottom: 14 }}>
        <div className="wm-filter">
          <span className="wm-filter-label">Interval</span>
          <RangePicker
            value={range}
            onChange={handleRangeChange}
            presets={RANGE_PRESETS}
            format="DD.MM.YYYY"
            allowClear={false}
          />
        </div>
        {sessionId ? (
          <div className="wm-filter">
            <span className="wm-filter-label">Sesiune (filtru comun)</span>
            <button
              type="button"
              className="wm-btn wm-btn-secondary wm-btn-sm"
              onClick={() => pickSession(null)}
              title="Renunță la filtrul de sesiune"
            >
              <span className="wm-log-email">{sessionId}</span> <CloseOutlined />
            </button>
          </div>
        ) : null}
        <div className="wm-filters-actions">
          <button type="button" className="wm-btn wm-btn-secondary wm-btn-sm" onClick={reloadAll}>
            <ReloadOutlined /> Reîncarcă
          </button>
        </div>
      </div>

      <div className="wm-stats">
        <div className="wm-stat">
          <div className="wm-stat-label">Request-uri</div>
          <div className="wm-stat-value">{t.requests ?? "—"}</div>
          <div className="wm-stat-meta">{t.events ?? 0} evenimente în interfață</div>
        </div>
        <div className={`wm-stat ${t.errors ? "wm-stat-warn" : "wm-stat-good"}`}>
          <div className="wm-stat-label">Erori (≥ 400)</div>
          <div className="wm-stat-value">{t.errors ?? "—"}</div>
          <div className="wm-stat-meta">refuzate, negăsite sau eșuate</div>
        </div>
        <div className={`wm-stat ${t.failedLogins ? "wm-stat-bad" : "wm-stat-good"}`}>
          <div className="wm-stat-label">Logări eșuate</div>
          <div className="wm-stat-value">{t.failedLogins ?? "—"}</div>
          <div className="wm-stat-meta">{t.logins ?? 0} autentificări reușite</div>
        </div>
        <div className="wm-stat">
          <div className="wm-stat-label">Utilizatori activi</div>
          <div className="wm-stat-value">{t.uniqueUsers ?? "—"}</div>
          <div className="wm-stat-meta">de pe {t.uniqueIps ?? 0} IP-uri distincte</div>
        </div>
      </div>

      <div className="wm-card wm-history" ref={cardRef}>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "sessions", label: `Sesiuni (${sessions.total})`, children: sessionsTab },
            { key: "requests", label: `Request-uri API (${requests.total})`, children: requestsTab },
            { key: "events", label: `Evenimente interfață (${events.total})`, children: eventsTab },
            { key: "routes", label: `Rute (${routes.length})`, children: routesTab },
            { key: "sources", label: "IP-uri și utilizatori", children: sourcesTab },
          ]}
        />
      </div>
    </div>
  );
};

const Toolbar = ({ total, what, active, note, onReset, onExport, onReload }) => (
  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 12 }}>
    <p className="wm-results-note" style={{ margin: 0, flex: "1 1 auto" }}>
      {total} {what} găsite{active ? " (filtre active)" : ""}
      {note ? <span style={{ color: "#8b93a1" }}> · {note}</span> : null}
    </p>
    {active ? (
      <button type="button" className="wm-btn wm-btn-ghost wm-btn-sm" onClick={onReset}>
        <ClearOutlined /> Resetează filtrele
      </button>
    ) : null}
    {onExport ? (
      <button
        type="button"
        className="wm-btn wm-btn-secondary wm-btn-sm"
        onClick={onExport}
        disabled={!total}
        title="Descarcă toate înregistrările din filtrul curent (nu doar pagina)"
      >
        <DownloadOutlined /> Export CSV
      </button>
    ) : null}
    <button type="button" className="wm-btn wm-btn-secondary wm-btn-sm" onClick={onReload}>
      <ReloadOutlined /> Reîncarcă
    </button>
  </div>
);

export default AuditLog;
